<template>
    <div class="mb-4 card card-default" v-if="showRequiredDocuments">
        <h5 class="card-header" id="required_documents">Required Documents</h5>

        <div class="text-center">
            <!-- Display any upload progress or status -->
            <div v-if="this.uploading">
                <div :style="{ width: progress + '%' }" class="progress-bar"></div>
            </div>
            <p v-if="this.uploadSuccess">Upload successful!</p>
            <p v-if="this.uploadError">Error: {{ uploadError }}</p>

            <!-- Loader element with Font Awesome spinner -->
            <div v-if="loading" class="spinner-container mx-4 my-4">
                <i class="fas fa-spinner fa-spin"></i>
            </div>
            <div v-if="errorMessage" class="error">{{ this.errorMessage }}</div>
        </div>
        <table class="table no-margin centered-content-table" v-if="!loading">
            <thead>
            <tr>
                <th>Document</th>
                <th>Status</th>
                <th class="text-right" >Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(documentType, index) in this.documentTypes" :key="index">
                <th class="col-md-4">
                    {{ documentType.name }}
                    <i
                        class="fas fa-info-circle fa-lg"
                        data-toggle="tooltip"
                        :title="documentType.description"
                        v-if="documentType.description !== ''"
                    ></i>
                </th>
                <th class="col-md-4">
                    <!-- Show Spinner when uploading -->
                    <span v-if="isDisabled(index)" class="fas fa-sync fa-spin"></span>
                    <!-- Show Status when not uploading -->
                    <span v-else>
                        <i
                            v-if="canShowDangerIcon(documentType.status)"
                            class="fas fa-exclamation-triangle text-danger">
                        </i>
                        <i
                            v-if="canShowCheckIcon(documentType.status)"
                            class="fas fa-check text-success">
                        </i>
                        <i
                            v-if="canShowPendingIcon(documentType.status)"
                            class="fas fa-hourglass-half text-primary">
                        </i>

                        {{ statusText(documentType.status) }}

                        <i
                            class="fas fa-info-circle fa-lg"
                            data-toggle="tooltip"
                            :title="documentType.reason"
                            v-if="documentType.reason !== ''"
                        ></i>
                    </span>
                </th>
                <th class="text-right">
                    <div v-if="canUploadFile(documentType.status)">
                        <button
                            class="btn btn-sm btn-primary upload-button glyphicon glyphicon-refresh glyphicon-spin"
                            @click="triggerFileInput(index)"
                            :disabled="isDisabled(index)"
                        >
                            {{ uploadDocumentText(documentType.status) }}
                        </button>
                        <p class="upload-description">
                            {{ uploadDocumentDescription(documentType.max_file_size) }}
                        </p>
                    </div>
                    <div v-if="canCancelDocument(documentType.status)">
                        <button
                            class="btn btn-sm btn-danger"
                            @click="cancelDocument(index)"
                            :disabled="isDisabled(index)"
                        >
                            Cancel Document
                        </button>
                    </div>
                </th>
            </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
export default {
    name: "ParticipantDocumentUploads",
    props: {
        registrationCategoryId: {
            required: true,
            type: Number,
        },
        participantId: {
            required: true,
            type: Number,
        },
    },
    data() {
        return {
            documentTypes: [],
            uploading: false,   // Track uploading state
            uploadSuccess: false, // Track upload success state
            uploadError: null,   // Store error message
            progress: 0,         // Store upload progress
            loading: false,
            errorMessage: '',
            disabledIndexes: [], // Array to track disabled indexes
            showRequiredDocuments: false,
        }
    },
    async mounted() {
        this.loading = true;

        try {
            const response = await this.$root.makeApiRequest(async (jwtToken) => {
                return await axios.get(`/api/v0/document_types_registration_categories/${this.registrationCategoryId}`, {
                    headers: {
                        'Authorization': jwtToken,
                    }
                });
            });

            if (response.data.data.length > 0) {
                this.showRequiredDocuments = true;

                const documentsResponse = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.get(`/api/v0/participant_documents/${this.participantId}`, {
                        headers: {
                            'Authorization': jwtToken,
                        }
                    });
                });

                this.documentTypes = documentsResponse.data.data;
                this.loading = false;
            }
        } catch (error) {
            this.loading = false;
            console.error('There was an error!', error);
        }
    },
    methods: {
        // Method to trigger file input dialog without using ref
        triggerFileInput(index) {
            // Create a file input element dynamically
            const fileInput = document.createElement('input');
            fileInput.type = 'file';

            // When the user selects a file, handle it
            fileInput.onchange = (event) => {
                const file = event.target.files[0];
                if (file) {
                    this.fileName = file.name;
                }

                this.handleFileChange(event, index)
            };

            // Trigger the file input dialog
            fileInput.click();
        },

        // Handle file selection
        handleFileChange(event, index) {
            const file = event.target.files[0]; // Get the first file

            if (file) {
                this.$set(this.documentTypes, index, { ...this.documentTypes[index], file });
                this.uploadFile(index);
            }
        },

        // Upload the file
        async uploadFile(index) {
            const documentType = this.documentTypes[index];

            if (!documentType.file) {
                alert('Please select a file for ' + documentType.name);
                return;
            }

            this.disabledIndexes.push(index); // Disable the button after it's clicked

            const formData = new FormData(); // Create FormData object
            formData.append('file', documentType.file); // Append the file to FormData
            formData.append('document_type_id', documentType.id);
            formData.append('participant_id', this.participantId);

            this.uploading = true; // Start uploading
            this.uploadSuccess = false; // Reset success flag
            this.uploadError = null; // Reset error flag
            this.progress = 0; // Reset progress

            let endPoint = '/api/v0/participant_documents/';
            if (documentType.document_id) {
                endPoint = `/api/v0/participant_documents/${documentType.document_id}`;
            }

            try {
                const response = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.post(endPoint, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data', // Important for file uploads
                            'Authorization': jwtToken,
                        },
                        onUploadProgress: (progressEvent) => {
                            if (progressEvent.total) {
                                this.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total); // Calculate upload progress
                            }
                        },
                    });
                });

                if (response.data.status === 'success') {
                    this.documentTypes[index].status = response.data.data.document_status;
                    this.documentTypes[index].reason = response.data.data.reason;
                    this.documentTypes[index].document_id = response.data.data.id;
                    this.errorMessage = '';
                } else {
                    const errors = response.data.data && response.data.data.file_path;

                    if (errors) {
                        // Loop through each key in the file_path object
                        for (const errorKey in errors) {
                            if (errors.hasOwnProperty(errorKey)) {
                                this.errorMessage = errors[errorKey];
                            }
                        }
                    } else {
                        this.errorMessage = 'Something went wrong, Please try again!';
                    }
                }

                this.uploading = false;
                this.disabledIndexes = [];
            } catch (error) {
                this.uploading = false;
                this.uploadError = error;
                this.disabledIndexes = [];
            }
        },

        canUploadFile(status) {
            return status === 'upload_required' || status === 'rejected' || status === 'cancelled';
        },

        canShowDangerIcon(status) {
            return status === 'upload_required' || status === 'rejected' || status === 'cancelled';
        },

        canShowCheckIcon(status) {
            return status === 'accepted';
        },

        canShowPendingIcon(status) {
            return status === 'pending';
        },

        canCancelDocument(status) {
            return status === 'pending';
        },

        statusText(status) {
            switch (status) {
                case 'pending':
                    return 'Document Awaiting Approval';
                case 'rejected':
                    return 'Rejected';
                case 'upload_required':
                    return 'Please Supply Document';
                case 'accepted':
                    return 'Accepted';
                case 'cancelled':
                    return 'Document Cancelled';
                default:
                    return 'Please Supply Document';
            }
        },

        uploadDocumentText(status) {
            return ['rejected', 'cancelled'].includes(status) ? 'Reupload Document' : 'Upload Document';
        },

        uploadDocumentDescription(maxfileSize) {
            return `File Size: ${maxfileSize} (jpeg, jpg, png, gif, pdf)`;
        },

        // Method to check if a button should be disabled
        isDisabled(index) {
            return this.disabledIndexes.includes(index); // Return true if the index is in the disabled array
        },

        // Cancel document method
        async cancelDocument(index) {
            const documentType = this.documentTypes[index];

            if (!documentType.document_id) {
                this.errorMessage = 'No document to cancel';
                return;
            }

            if (!confirm('Are you sure you want to cancel this document?')) {
                return;
            }

            this.disabledIndexes.push(index); // Disable the button while processing

            try {
                const response = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.patch(`/api/v0/participant_documents/${documentType.document_id}/cancel`, {}, {
                        headers: {
                            'Authorization': jwtToken,
                            'Content-Type': 'application/json'
                        }
                    });
                });

                if (response.data.status === 'success') {
                    // Update the document status to cancelled after successful cancellation
                    this.documentTypes[index].status = 'cancelled';
                    this.documentTypes[index].reason = '';
                    this.errorMessage = '';
                } else {
                    this.errorMessage = 'Failed to cancel document. Please try again.';
                }

                this.disabledIndexes = this.disabledIndexes.filter(i => i !== index);
            } catch (error) {
                // Check if error response contains a specific message
                if (error.response && error.response.data && error.response.data.message) {
                    this.errorMessage = error.response.data.message;
                } else {
                    this.errorMessage = 'Error cancelling document. Please try again.';
                }
                this.disabledIndexes = this.disabledIndexes.filter(i => i !== index);
            }
        },
    },
}
</script>

<style scoped>
.progress-bar {
    height: 20px;
    background-color: green;
    transition: width 0.2s ease;
}
.error {
    color: red;
}
.upload-description {
    font-size: smaller;
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Participants/ParticipantDocumentUploads.vue